import React from 'react';
import { ShieldAlert, X } from 'lucide-react';
import { getImpersonation, clearImpersonation } from '../App';

/**
 * Sticky banner shown at the very top of the app when this tab is running
 * under a Super Admin "Open as user" session. The banner serves three
 * purposes:
 *   1. Makes it impossible to forget you're impersonating (and accidentally
 *      take an action as another user without realising).
 *   2. One-click exit — clears the per-tab token and closes the tab.
 *   3. Distinct red styling so any screenshot of an impersonation session
 *      clearly identifies itself in the audit trail.
 */
const ImpersonationBanner = () => {
  const imp = getImpersonation();
  if (!imp) return null;

  const exit = () => {
    clearImpersonation();
    // Closing the tab is the cleanest exit; if the browser blocks it
    // (e.g. the tab wasn't opened by JS), fall back to navigating away.
    try { window.close(); } catch {}
    setTimeout(() => { window.location.href = '/'; }, 50);
  };

  return (
    <div
      className="w-full bg-[#8B4444] text-white px-4 py-2 flex items-center justify-center gap-3 text-xs font-medium relative z-[60] shadow-md"
      data-testid="impersonation-banner"
    >
      <ShieldAlert className="w-4 h-4" />
      <span>
        Viewing as <strong>{imp.name || imp.email}</strong>
        {imp.role && <span className="opacity-80 ml-1">· {imp.role.replace(/_/g, ' ')}</span>}
        <span className="opacity-70 ml-2">— Super Admin impersonation session</span>
      </span>
      <button
        onClick={exit}
        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 hover:bg-white/25 transition-colors"
        data-testid="impersonation-exit-btn"
      >
        <X className="w-3 h-3" />
        Exit
      </button>
    </div>
  );
};

export default ImpersonationBanner;
